import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BlobPreloader } from '../components/BlobPreloader';
import TrendInsights from '../components/TrendInsights';
import { 
  Phone, 
  Users, 
  Target, 
  Zap,
  RefreshCw,
  Filter,
  Calendar,
  X,
  ThumbsUp,
  ThumbsDown,
  ArrowRight,
  Package,
  TrendingUp,
  MessageSquareQuote,
  Flame,
  Snowflake,
  Sun,
  HelpCircle,
  Building2,
  Swords,
  ChevronDown,
  ChevronUp,
  Clock,
  Clock4
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// One expandable row inside a CategoryVerdictCard. Clicking the chevron
// reveals a nested list of sub-categories (each linkable to Call Logs).
const CategoryRow = ({ row, testid, hoverBg, hoverText, bars, maxCount, buildFilteredLink, verdict, tier }) => {
  const [open, setOpen] = React.useState(false);
  const subs = row.subcategories || [];
  const hasSubs = subs.length > 0 && !(subs.length === 1 && subs[0].name === 'Uncategorized');
  const _base = { qcCategory: row.category };
  if (verdict) _base.verdict = verdict;
  if (tier) _base.tier = tier;
  const catLink = buildFilteredLink(_base);
  return (
    <div>
      <div
        className={`group flex items-center gap-3 -mx-2 px-2 py-1.5 rounded-lg transition-colors ${hoverBg}`}
        data-testid={`${testid}-row-${row.category}`}
      >
        {hasSubs ? (
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            className="p-0.5 -ml-1 rounded hover:bg-white/60"
            data-testid={`${testid}-toggle-${row.category}`}
            aria-label={open ? 'Collapse sub-categories' : 'Expand sub-categories'}
          >
            {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
          </button>
        ) : (
          <span className="w-3.5" />
        )}
        <Link to={catLink} className={`text-sm text-slate-700 flex-1 truncate transition-colors ${hoverText}`}>
          {row.category}
        </Link>
        <div className="w-32 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full bg-gradient-to-r ${bars} rounded-full transition-all`} style={{ width: `${(row.count / maxCount) * 100}%` }} />
        </div>
        <span className="text-sm font-semibold text-slate-700 w-8 text-right tabular-nums">{row.count}</span>
      </div>
      {open && hasSubs && (
        <div className="pl-6 mt-1 space-y-1" data-testid={`${testid}-subs-${row.category}`}>
          {subs.map(sub => {
            const subBase = { qcCategory: row.category, qcSubcategory: sub.name };
            if (verdict) subBase.verdict = verdict;
            if (tier) subBase.tier = tier;
            return (
              <Link
                key={sub.name}
                to={buildFilteredLink(subBase)}
                className="flex items-center gap-3 px-2 py-1 rounded-md hover:bg-slate-50 text-[13px] text-slate-600"
                data-testid={`${testid}-subrow-${row.category}-${sub.name}`}
              >
                <span className="w-1 h-1 rounded-full bg-slate-400" />
                <span className="flex-1 truncate">{sub.name}</span>
                <span className="text-xs font-semibold text-slate-500 tabular-nums">{sub.count}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Reusable Dashboard card — header strip + category bar list.
// Each row links to the Call Logs filtered by verdict + qcCategory.
const CategoryVerdictCard = ({
  testid, title, subtitle, icon, gradient, subtitleClass,
  bars, hoverBg, hoverText, verdict, tier, rows, buildFilteredLink, viewAllTestId,
}) => {
  const maxCount = Math.max(...rows.map(r => r.count), 1);
  const linkAll = buildFilteredLink({
    ...(verdict ? { verdict } : {}),
    ...(tier ? { tier } : {}),
  });
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col" data-testid={testid}>
      <div className={`bg-gradient-to-r ${gradient} p-5`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
              {icon}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{title}</h2>
              <p className={`text-sm ${subtitleClass}`}>{subtitle}</p>
            </div>
          </div>
          {verdict !== null && (
            <Link
              to={linkAll}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              data-testid={viewAllTestId}
            >
              <ArrowRight className="w-4 h-4 text-white" />
            </Link>
          )}
        </div>
      </div>
      <div className="p-5 flex-1">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-4">Call Categories</p>
        {rows.length === 0 ? (
          <div className="text-center py-8">
            <TrendingUp className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No data yet</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {rows.map((row) => (
              <CategoryRow
                key={row.category}
                row={row}
                testid={testid}
                hoverBg={hoverBg}
                hoverText={hoverText}
                bars={bars}
                maxCount={maxCount}
                buildFilteredLink={buildFilteredLink}
                verdict={verdict}
                tier={tier}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [agents, setAgents] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [noBusinessNeedExpanded, setNoBusinessNeedExpanded] = useState(false);
  
  // Filters
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedDataSource, setSelectedDataSource] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filtersActive, setFiltersActive] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams();
      if (selectedAgent) params.append('agent_id', selectedAgent);
      if (selectedDataSource) params.append('data_source', selectedDataSource);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const [analyticsRes, agentsRes, sourcesRes] = await Promise.all([
        axios.get(`${API}/dashboard/analytics${queryString}`),
        axios.get(`${API}/agents`),
        axios.get(`${API}/data-sources`)
      ]);
      setAnalytics(analyticsRes.data);
      setAgents(agentsRes.data);
      setDataSources(sourcesRes.data || []);
      setLastUpdated(new Date());
      setFiltersActive(!!selectedAgent || !!selectedDataSource || !!dateFrom || !!dateTo);
    } catch (error) {
      console.error('Error fetching data:', error);
      setFetchError(error.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [selectedAgent, selectedDataSource, dateFrom, dateTo]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const clearFilters = () => {
    setSelectedAgent('');
    setSelectedDataSource('');
    setDateFrom('');
    setDateTo('');
  };

  // Build filtered link with support for sub-category, product, and brand filters
  const buildFilteredLink = (baseParams = {}) => {
    const params = new URLSearchParams();
    if (baseParams.verdict) params.append('verdict', baseParams.verdict);
    if (baseParams.stage) params.append('stage', baseParams.stage);
    if (baseParams.buyingStage) params.append('buyingStage', baseParams.buyingStage);
    if (baseParams.subCategory) params.append('subCategory', baseParams.subCategory);
    if (baseParams.product) params.append('product', baseParams.product);
    if (baseParams.brand) params.append('brand', baseParams.brand);
    if (baseParams.competitorBrand) params.append('competitorBrand', baseParams.competitorBrand);
    if (baseParams.qcCategory) params.append('qcCategory', baseParams.qcCategory);
    if (baseParams.qcSubcategory) params.append('qcSubcategory', baseParams.qcSubcategory);
    if (baseParams.tier) params.append('tier', baseParams.tier);
    if (selectedAgent) params.append('agent', selectedAgent);
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    const queryString = params.toString();
    return `/call-logs${queryString ? `?${queryString}` : ''}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="loading-dashboard">
        <BlobPreloader size={88} label="Loading insights..." />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="error-dashboard">
        <div className="text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <RefreshCw className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-700 mb-2">Unable to load dashboard</h3>
          <p className="text-slate-500 text-sm mb-2">There was a problem fetching the analytics data.</p>
          {fetchError && (
            <p className="text-rose-500 text-xs mb-4 font-mono bg-rose-50 px-3 py-1 rounded">{fetchError}</p>
          )}
          <button
            onClick={() => fetchData(true)}
            className="px-4 py-2 bg-[#2eb4ac] text-white rounded-lg hover:bg-[#259790] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Analysed = Interested + Not Interested + Partially Interested + Other Concerns.
  // Unanalysed is excluded so verdict percentages reflect completed AI review only.
  const partiallyInterestedCount = analytics.partially_interested_count || 0;
  const otherConcernsCount = analytics.other_concerns_count || 0;
  const analysedCount = (analytics.interested_count || 0) + (analytics.not_interested_count || 0) + partiallyInterestedCount + otherConcernsCount;
  const notSpecifiedCount = Math.max(0, analysedCount - (analytics.interested_specified || 0) - (analytics.not_interested_specified || 0));
  const interestedPct = analysedCount > 0 ? Math.round((analytics.interested_count / analysedCount) * 100) : 0;
  const notInterestedPct = analysedCount > 0 ? Math.round((analytics.not_interested_count / analysedCount) * 100) : 0;
  const partiallyInterestedPct = analysedCount > 0 ? Math.round((partiallyInterestedCount / analysedCount) * 100) : 0;
  const otherConcernsPct = analysedCount > 0 ? Math.round((otherConcernsCount / analysedCount) * 100) : 0;

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-0.5">Real-time sales intelligence</p>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-slate-400 hidden sm:flex items-center gap-1">
              <RefreshCw className="w-3 h-3" />
              {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => fetchData(false)}
            className="p-2 text-slate-400 hover:text-[#2eb4ac] hover:bg-slate-50 rounded-lg transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <Link
            to="/upload"
            className="flex items-center gap-2 px-4 py-2 bg-[#2eb4ac] text-white text-sm font-medium rounded-lg hover:bg-[#269d96] transition-colors"
            data-testid="upload-calls-btn"
          >
            <Zap className="w-4 h-4" />
            Upload Calls
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-slate-500">
            <Filter className="w-4 h-4" />
            <span className="text-sm font-medium">Filters</span>
          </div>
          
          <select
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac] bg-white"
            data-testid="agent-filter"
          >
            <option value="">All Agents</option>
            {agents.map(agent => (
              <option key={agent.id} value={agent.id}>{agent.name}</option>
            ))}
          </select>

          <select
            value={selectedDataSource}
            onChange={(e) => setSelectedDataSource(e.target.value)}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac] bg-white"
            data-testid="data-source-filter"
          >
            <option value="">All Sources</option>
            {dataSources.map(s => (
              <option key={s.id} value={s.name}>{s.name}</option>
            ))}
          </select>
          
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac]"
              data-testid="date-from"
            />
            <span className="text-slate-400 text-sm">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac]"
              data-testid="date-to"
            />
          </div>
          
          {filtersActive && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-2 py-1.5 text-sm text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
              data-testid="clear-filters-btn"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 lg:grid-cols-7 gap-4">
        {/* Total Calls */}
        <Link to={buildFilteredLink()} className="group bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all" data-testid="total-calls-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Total Calls</p>
              <p className="text-2xl font-semibold text-slate-900 mt-1">{analytics.total_calls}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
              <Phone className="w-5 h-5 text-slate-600" />
            </div>
          </div>
        </Link>

        {/* Unanalysed */}
        <Link
          to={buildFilteredLink({ verdict: 'unanalysed' })}
          className="group bg-white rounded-xl border border-slate-200 p-4 hover:border-amber-300 hover:shadow-sm transition-all"
          data-testid="unanalyzed-calls-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Unanalysed</p>
              <p className="text-2xl font-semibold text-amber-600 mt-1">{analytics.unanalyzed_calls ?? 0}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </Link>
        
        {/* Interested */}
        <Link to={buildFilteredLink({ tier: 'Interested' })} className="group bg-white rounded-xl border border-slate-200 p-4 hover:border-emerald-200 hover:shadow-sm transition-all" data-testid="interested-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Interested</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className="text-2xl font-semibold text-emerald-600">{analytics.interested_count}</p>
                <span className="text-sm text-emerald-600 font-medium">{interestedPct}%</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {analytics.interested_specified || 0} categorized
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
              <ThumbsUp className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </Link>
        
        {/* Not Interested */}
        <Link to={buildFilteredLink({ tier: 'Not Interested' })} className="group bg-white rounded-xl border border-slate-200 p-4 hover:border-rose-200 hover:shadow-sm transition-all" data-testid="not-interested-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Not Interested</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className="text-2xl font-semibold text-rose-600">{analytics.not_interested_count}</p>
                <span className="text-sm text-rose-600 font-medium">{notInterestedPct}%</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {analytics.not_interested_specified || 0} categorized
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center group-hover:bg-rose-100 transition-colors">
              <ThumbsDown className="w-5 h-5 text-rose-600" />
            </div>
          </div>
        </Link>

        {/* Partially Interested — driven by qc_category = "Call Back Later" regardless of AI verdict */}
        <Link
          to={buildFilteredLink({ tier: 'Partially Interested' })}
          className="group bg-white rounded-xl border border-slate-200 p-4 hover:border-amber-300 hover:shadow-sm transition-all"
          data-testid="partially-interested-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Partially Interested</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className="text-2xl font-semibold text-amber-600">{partiallyInterestedCount}</p>
                <span className="text-sm text-amber-600 font-medium">{partiallyInterestedPct}%</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Call Back Later
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </Link>

        {/* Other Concerns — operational, non-sales (payment ops, sponsorships) */}
        <Link
          to={buildFilteredLink({ tier: 'Other Concerns' })}
          className="group bg-white rounded-xl border border-slate-200 p-4 hover:border-purple-300 hover:shadow-sm transition-all"
          data-testid="other-concerns-card"
          title="Operational calls — click to see all rerouted rows"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Other Concerns</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className="text-2xl font-semibold text-purple-600">{otherConcernsCount}</p>
                <span className="text-sm text-purple-600 font-medium">{otherConcernsPct}%</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Payment / Billing Ops
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition-colors">
              <Clock4 className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </Link>
        
        {/* Active Agents */}
        <Link to="/settings" className="group bg-white rounded-xl border border-slate-200 p-4 hover:border-blue-200 hover:shadow-sm transition-all" data-testid="agents-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Active Agents</p>
              <p className="text-2xl font-semibold text-slate-900 mt-1">{analytics.agent_performance?.length || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </Link>
      </div>

      {/* Main Insight Cards — categories under each verdict tier (2x2 grid) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Interested */}
        <CategoryVerdictCard
          testid="interested-category-card"
          title="Interested"
          subtitle={`${analytics.interested_category_total || 0} calls landed here`}
          icon={<ThumbsUp className="w-5 h-5 text-white" />}
          gradient="from-emerald-500 to-emerald-600"
          subtitleClass="text-emerald-100"
          bars="from-emerald-400 to-emerald-500"
          hoverBg="hover:bg-emerald-50/50"
          hoverText="group-hover:text-emerald-700"
          verdict="Interested"
          tier="Interested"
          rows={analytics.interested_category_breakdown || []}
          buildFilteredLink={buildFilteredLink}
          viewAllTestId="view-interested-link"
        />

        {/* Not Interested */}
        <CategoryVerdictCard
          testid="not-interested-category-card"
          title="Not Interested"
          subtitle={`${analytics.not_interested_category_total || 0} calls landed here`}
          icon={<ThumbsDown className="w-5 h-5 text-white" />}
          gradient="from-rose-500 to-rose-600"
          subtitleClass="text-rose-100"
          bars="from-rose-400 to-rose-500"
          hoverBg="hover:bg-rose-50/50"
          hoverText="group-hover:text-rose-700"
          verdict="Not Interested"
          tier="Not Interested"
          rows={analytics.not_interested_category_breakdown || []}
          buildFilteredLink={buildFilteredLink}
          viewAllTestId="view-not-interested-link"
        />

        {/* Partially Interested */}
        <CategoryVerdictCard
          testid="partially-interested-category-card"
          title="Partially Interested"
          subtitle={`${analytics.partially_interested_category_total || 0} calls landed here`}
          icon={<Clock4 className="w-5 h-5 text-white" />}
          gradient="from-amber-400 to-amber-500"
          subtitleClass="text-amber-50"
          bars="from-amber-400 to-amber-500"
          hoverBg="hover:bg-amber-50/50"
          hoverText="group-hover:text-amber-700"
          verdict={null}
          tier="Partially Interested"
          rows={analytics.partially_interested_category_breakdown || []}
          buildFilteredLink={buildFilteredLink}
          viewAllTestId="view-partially-interested-link"
        />

        {/* Other Concerns — operational, non-sales pipeline */}
        <CategoryVerdictCard
          testid="other-concerns-category-card"
          title="Other Concerns"
          subtitle={`${analytics.other_concerns_total || 0} calls landed here`}
          icon={<Clock className="w-5 h-5 text-white" />}
          gradient="from-purple-500 to-purple-600"
          subtitleClass="text-purple-100"
          bars="from-purple-400 to-purple-500"
          hoverBg="hover:bg-purple-50/50"
          hoverText="group-hover:text-purple-700"
          verdict={null}
          tier="Other Concerns"
          rows={analytics.other_concerns_category_breakdown || []}
          buildFilteredLink={buildFilteredLink}
          viewAllTestId="view-other-concerns-link"
        />
      </div>


      {/* Trend Insights — live weekly trends from /api/dashboard/trends */}
      <TrendInsights agentId={selectedAgent} dataSource={selectedDataSource} />


      {/* Second Row - Pipeline & Products/Brands (4 columns) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        
        {/* Pipeline & Leads */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="pipeline-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Pipeline & Leads</h2>
              <p className="text-sm text-slate-500">Lead temperature distribution</p>
            </div>
          </div>
          
          {/* Temperature Pills */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <Link 
              to={buildFilteredLink({ stage: 'Hot' })}
              className="bg-gradient-to-br from-orange-50 to-red-50 border border-orange-100 rounded-xl p-4 text-center hover:shadow-sm hover:border-orange-200 transition-all"
            >
              <Flame className="w-5 h-5 text-orange-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-orange-600">{analytics.pipeline?.hot_leads || 0}</p>
              <p className="text-xs text-orange-600 font-medium">Hot</p>
            </Link>
            <Link 
              to={buildFilteredLink({ stage: 'Warm' })}
              className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-100 rounded-xl p-4 text-center hover:shadow-sm hover:border-amber-200 transition-all"
            >
              <Sun className="w-5 h-5 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-amber-600">{analytics.pipeline?.warm_leads || 0}</p>
              <p className="text-xs text-amber-600 font-medium">Warm</p>
            </Link>
            <Link 
              to={buildFilteredLink({ stage: 'Cold' })}
              className="bg-gradient-to-br from-blue-50 to-sky-50 border border-blue-100 rounded-xl p-4 text-center hover:shadow-sm hover:border-blue-200 transition-all"
            >
              <Snowflake className="w-5 h-5 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-blue-600">{analytics.pipeline?.cold_leads || 0}</p>
              <p className="text-xs text-blue-600 font-medium">Cold</p>
            </Link>
          </div>
          
          {/* Buying Stages */}
          {analytics.pipeline?.buying_stages && Object.keys(analytics.pipeline.buying_stages).length > 0 && (
            <div className="pt-4 border-t border-slate-100">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Buying Stage</p>
              <div className="space-y-2.5">
                {Object.entries(analytics.pipeline.buying_stages).map(([stage, count]) => (
                  <Link 
                    key={stage} 
                    to={buildFilteredLink({ buyingStage: stage })}
                    className="flex items-center gap-3 hover:bg-indigo-50/50 -mx-2 px-2 py-1 rounded-lg transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-slate-600 w-32 truncate group-hover:text-indigo-700 transition-colors">{stage}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-indigo-400 to-indigo-500 rounded-full"
                        style={{ width: `${analytics.total_calls > 0 ? (count / analytics.total_calls * 100) : 0}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-slate-700 w-6 text-right">{count}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Top Products Discussed */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="products-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Top Products</h2>
              <p className="text-sm text-slate-500">Most mentioned products</p>
            </div>
          </div>
          
          <div className="space-y-3">
            {analytics.product_frequency && analytics.product_frequency.length > 0 ? (
              analytics.product_frequency.slice(0, 5).map((product, idx) => (
                <Link 
                  key={idx} 
                  to={buildFilteredLink({ product: product.product })}
                  className="flex items-center gap-3 hover:bg-violet-50/50 -mx-2 px-2 py-1 rounded-lg transition-colors cursor-pointer group"
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 transition-colors ${
                    idx === 0 ? 'bg-violet-100 text-violet-700 group-hover:bg-violet-200' :
                    idx === 1 ? 'bg-violet-50 text-violet-600 group-hover:bg-violet-100' :
                    'bg-slate-100 text-slate-600 group-hover:bg-slate-200'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate group-hover:text-violet-700 transition-colors">{product.product}</p>
                    <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-violet-400 to-purple-500 rounded-full"
                        style={{ width: `${Math.min((product.count / (analytics.product_frequency[0]?.count || 1)) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-slate-500 w-8 text-right">{product.count}</span>
                </Link>
              ))
            ) : (
              <div className="text-center py-8">
                <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No products detected yet</p>
                <p className="text-xs text-slate-400 mt-1">Upload calls to see product insights</p>
              </div>
            )}
          </div>
        </div>

        {/* Wellversed Brands */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="brands-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Wellversed Brands</h2>
              <p className="text-sm text-slate-500">Our brands mentioned in calls</p>
            </div>
          </div>
          
          <div className="space-y-3">
            {analytics.brand_frequency && analytics.brand_frequency.length > 0 ? (
              analytics.brand_frequency.slice(0, 5).map((brand, idx) => (
                <Link 
                  key={idx} 
                  to={buildFilteredLink({ brand: brand.brand })}
                  className="flex items-center gap-3 hover:bg-cyan-50/50 -mx-2 px-2 py-1 rounded-lg transition-colors cursor-pointer group"
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 transition-colors ${
                    idx === 0 ? 'bg-cyan-100 text-cyan-700 group-hover:bg-cyan-200' :
                    idx === 1 ? 'bg-cyan-50 text-cyan-600 group-hover:bg-cyan-100' :
                    'bg-slate-100 text-slate-600 group-hover:bg-slate-200'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate group-hover:text-cyan-700 transition-colors">{brand.brand}</p>
                    <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-cyan-400 to-teal-500 rounded-full"
                        style={{ width: `${Math.min((brand.count / (analytics.brand_frequency[0]?.count || 1)) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-slate-500 w-8 text-right">{brand.count}</span>
                </Link>
              ))
            ) : (
              <div className="text-center py-8">
                <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No brands detected yet</p>
                <p className="text-xs text-slate-400 mt-1">Add brands in Settings</p>
              </div>
            )}
          </div>
        </div>

        {/* Competitor Brands */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="competitor-brands-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
              <Swords className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Competitor Brands</h2>
              <p className="text-sm text-slate-500">Competitors mentioned in calls</p>
            </div>
          </div>
          
          <div className="space-y-3">
            {analytics.competitor_brand_frequency && analytics.competitor_brand_frequency.length > 0 ? (
              analytics.competitor_brand_frequency.slice(0, 5).map((brand, idx) => (
                <Link 
                  key={idx} 
                  to={buildFilteredLink({ competitorBrand: brand.brand })}
                  className="flex items-center gap-3 hover:bg-orange-50/50 -mx-2 px-2 py-1 rounded-lg transition-colors cursor-pointer group"
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 transition-colors ${
                    idx === 0 ? 'bg-orange-100 text-orange-700 group-hover:bg-orange-200' :
                    idx === 1 ? 'bg-orange-50 text-orange-600 group-hover:bg-orange-100' :
                    'bg-slate-100 text-slate-600 group-hover:bg-slate-200'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate group-hover:text-orange-700 transition-colors">{brand.brand}</p>
                    <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-orange-400 to-red-500 rounded-full"
                        style={{ width: `${Math.min((brand.count / (analytics.competitor_brand_frequency[0]?.count || 1)) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-slate-500 w-8 text-right">{brand.count}</span>
                </Link>
              ))
            ) : (
              <div className="text-center py-8">
                <Swords className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No competitors detected yet</p>
                <p className="text-xs text-slate-400 mt-1">Competitor mentions will appear here</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
